/*
 * Bulk pricing on the product page.
 * - <order-total> (snippets/product-order-total.liquid): live total + savings above the form buttons.
 * - <bulk-discount-progress> (snippets/bulk-discounts.liquid): progress bar, next-tier hint
 *   and applied-tier highlight inside the bulk discount table.
 * Both follow the quantity input (plus/minus buttons and typing). Tiers are read from
 * .bls__bulk-discounts-tier[data-qty][data-percent]. main-product.js calls
 * OrderTotal.setPrice() on variant change.
 * Loaded by both snippets, so everything is defined once.
 */
if (!window.BlsBulkPricing) {
  window.BlsBulkPricing = {
    getScope(el) {
      return (
        el.closest(".bls__product-details-infor") ||
        el.closest(".shopify-section") ||
        document
      );
    },

    getQty(input) {
      return Math.max(parseInt(input && input.value, 10) || 1, 1);
    },

    // All tiers with a quantity, in ascending order; percent is 0 for text tiers ("Ask for a price").
    getTiers(scope) {
      return Array.from(scope.querySelectorAll(".bls__bulk-discounts-tier[data-qty]"))
        .map((el) => ({
          el,
          qty: parseInt(el.dataset.qty, 10) || 0,
          percent: parseFloat(el.dataset.percent) || 0,
        }))
        .filter((tier) => tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);
    },

    // Applied = highest discount tier reached; next = first discount tier not reached yet.
    resolve(tiers, qty) {
      let applied = null;
      let next = null;
      tiers.forEach((tier) => {
        if (tier.percent <= 0) return;
        if (qty >= tier.qty) applied = tier;
        else if (!next) next = tier;
      });
      return { applied, next };
    },

    format(cents) {
      return Shopify.formatMoney(cents, cartStrings.money_format);
    },
  };

  class BulkQuantityListener extends HTMLElement {
    connectedCallback() {
      this.scope = BlsBulkPricing.getScope(this);
      this.input = document.getElementById(this.dataset.input);
      this.update = this.update.bind(this);
      if (this.input) {
        this.input.addEventListener("input", this.update);
        this.input.addEventListener("change", this.update);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener("input", this.update);
        this.input.removeEventListener("change", this.update);
      }
    }
  }

  class OrderTotal extends BulkQuantityListener {
    connectedCallback() {
      this.valueEl = this.querySelector(".bls__order-total-value");
      this.compareEl = this.querySelector(".bls__order-total-compare");
      this.savingsEl = this.querySelector(".bls__order-total-savings");
      super.connectedCallback();
    }

    setPrice(price) {
      this.dataset.price = price;
      this.update();
    }

    update() {
      const price = parseInt(this.dataset.price, 10) || 0;
      const qty = BlsBulkPricing.getQty(this.input);
      const { applied } = BlsBulkPricing.resolve(BlsBulkPricing.getTiers(this.scope), qty);

      const subtotal = price * qty;
      const percent = applied ? applied.percent : 0;
      const total = Math.round((subtotal * (100 - percent)) / 100);
      const savings = subtotal - total;

      this.valueEl.innerHTML = BlsBulkPricing.format(total);
      this.compareEl.hidden = savings <= 0;
      this.savingsEl.hidden = savings <= 0;
      if (savings > 0) {
        this.compareEl.innerHTML = BlsBulkPricing.format(subtotal);
        this.savingsEl.innerHTML = `${this.dataset.labelSave} <strong>${BlsBulkPricing.format(
          savings
        )}</strong> (${percent}%)`;
      }
    }
  }

  class BulkDiscountProgress extends BulkQuantityListener {
    connectedCallback() {
      this.track = this.querySelector(".bls__bulk-discounts-progress-track");
      this.text = this.querySelector(".bls__bulk-discounts-progress-text");
      super.connectedCallback();
    }

    update() {
      const tiers = BlsBulkPricing.getTiers(this.closest(".bls__bulk-discounts"));
      // Needs the product form quantity input and at least one discount tier.
      if (!this.input || !tiers.some((tier) => tier.percent > 0)) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const qty = BlsBulkPricing.getQty(this.input);
      const { applied, next } = BlsBulkPricing.resolve(tiers, qty);

      // One segment per tier column, filling from the previous tier's quantity to this one.
      if (this.track.children.length !== tiers.length) {
        this.track.innerHTML = tiers
          .map(() => '<span class="bls__bulk-discounts-progress-segment"><span></span></span>')
          .join("");
      }
      let previousQty = 0;
      tiers.forEach((tier, index) => {
        const span = tier.qty - previousQty;
        const fill = Math.min(Math.max((qty - previousQty) / span, 0), 1);
        this.track.children[index].style.setProperty("--fill", fill);
        previousQty = tier.qty;
      });

      if (next) {
        this.text.textContent = this.dataset.labelNext
          .replace("{{ count }}", next.qty - qty)
          .replace("{{ percent }}", next.percent);
      } else {
        this.text.textContent = this.dataset.labelBest.replace("{{ percent }}", applied.percent);
      }
      this.text.classList.toggle("is-best", !next);

      tiers.forEach((tier) => {
        tier.el.classList.toggle("is-applied", tier === applied);
        if (tier === applied) tier.el.dataset.appliedLabel = this.dataset.labelApplied;
      });
    }
  }

  customElements.define("order-total", OrderTotal);
  customElements.define("bulk-discount-progress", BulkDiscountProgress);
}
